/**
 * Injection Screen Store — the shadow screener's per-task notice (W4-2).
 *
 * When a turn has read third-party content and the agent is about to act
 * outward, the server screens the step; if the verdict is "instructed by that
 * content rather than by the user", it pushes ONE notice here. SHADOW: the step
 * already ran, so a notice is something to read and dismiss, never something to
 * act on, and its absence proves nothing.
 *
 * Keyed by task, because the notice belongs to the conversation it happened in.
 * A later notice for the same task replaces the earlier one: the banner is a
 * signal, and the durable record is `injection_screen_events` plus the admin
 * card. Dismissal is in-memory on purpose — notices arrive only over the socket
 * and are never refetched, so nothing can resurrect a dismissed one.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface InjectionScreenNotice {
  taskId: string;
  toolUseId: string;
  toolName: string;
  /** The side-effect family the classifier assigned (drives the plain-words label). */
  family: string;
  /** The screener's one-line rationale, already bounded server-side. May be blank. */
  reason: string;
  /** ISO timestamp of the verdict. */
  at: string;
}

interface InjectionScreenStoreState {
  /** Latest undismissed notice per task. */
  noticesByTask: Record<string, InjectionScreenNotice>;
}

interface InjectionScreenStoreActions {
  /** Handle an incoming notice from the socket. */
  handleNoticeReceived: (notice: InjectionScreenNotice) => void;
  /** Dismiss the notice showing for one task. */
  dismiss: (taskId: string) => void;
}

type InjectionScreenStore = InjectionScreenStoreState &
  InjectionScreenStoreActions;

export const useInjectionScreenStore = create<InjectionScreenStore>()(
  devtools(
    (set) => ({
      noticesByTask: {},

      handleNoticeReceived: (notice: InjectionScreenNotice) => {
        if (!notice?.taskId) return;
        set((state) => ({
          noticesByTask: { ...state.noticesByTask, [notice.taskId]: notice },
        }));
      },

      dismiss: (taskId: string) => {
        set((state) => {
          if (!(taskId in state.noticesByTask)) return state;
          const next = { ...state.noticesByTask };
          delete next[taskId];
          return { noticesByTask: next };
        });
      },
    }),
    {
      name: "InjectionScreenStore",
      enabled: process.env.NODE_ENV === "development",
    }
  )
);
